import { deriveBotSeed, selectBotFallbackMove, type BotDecision } from "../../../bot";
import {
  applyLegacyRecordedMove,
  applyMove,
  applyVersion2RecordedMove,
  applyVersion3RecordedMove,
  cellAt,
  coordinate,
  createGameState,
  createInitialGame,
  getLegacyLegalMoves,
  getLegalMoves,
  getVersion2LegalMoves,
  getVersion3LegalMoves,
  isThreefoldRepetition,
  moveNotation,
  opponentOf,
  parseGame,
  parseLegacyGame,
  parseVersion2Game,
  parseVersion3Game,
  positionKey,
  validateGameState,
  type Coordinate,
  type GameState,
  type Move,
  type Player,
  type Transition,
} from "../../../game";
import {
  appendRecordedMove,
  completeRecording,
  createMatchRecord,
  createMatchRecording,
  createSavedMatch,
  LEGACY_MATCH_RULES_VERSION,
  CLEANUP_PASSAGE_MATCH_RULES_VERSION,
  MATCH_RULES_VERSION,
  PRE_REPETITION_MATCH_RULES_VERSION,
  usesRepetitionRules,
  MAX_SAVED_MATCH_REVISION,
  sameMatchVersion,
  replayRecording,
  versionOf,
  type CommitResult,
  type LoadMatchResult,
  type MatchResult,
  type MatchRecordV1,
  type MatchRecordingV1,
  type MatchVersion,
  type MatchWrite,
  type SavedMatchParseError,
  type SavedMatchV3,
  type StorageFailure,
  type StoragePort,
} from "../persistence";
import type { MatchMode } from "../matchMode";

import type {
  AnimationPort,
  BotPort,
  MatchActor,
  MatchAnnouncement,
  MatchCommandResult,
  MatchControllerOptions,
  MatchControllerSnapshot,
  MatchClock,
  MatchEpochFactory,
  MatchFocusTarget,
  MatchPauseReason,
  MatchOwnershipPort,
  MatchOwnershipSnapshot,
  MatchPersistenceState,
  MatchPhase,
  MatchPresentation,
  MatchReviewTimeline,
  MatchReviewTransition,
  MatchRoute,
  MatchSeedFactory,
  MatchSetupSelection,
  MatchTransitionPresentation,
} from "./types";

type Listener = () => void;

type AnnouncementInput = MatchAnnouncement extends infer Announcement
  ? Announcement extends Readonly<{ sequence: number }>
    ? Omit<Announcement, "sequence">
    : never
  : never;

type LiveMatch = Readonly<{
  saved: SavedMatchV3;
  game: GameState;
  result: MatchResult | null;
  positionKeys: readonly string[];
}>;

type MatchHistoryFrame = Readonly<{
  live: LiveMatch;
  presentation: MatchTransitionPresentation;
}>;

type MatchHistoryMutation = Readonly<
  | Readonly<{ kind: "append"; frame: MatchHistoryFrame }>
  | Readonly<{ kind: "restore"; history: readonly MatchHistoryFrame[] }>
  | Readonly<{ kind: "clear" }>
>;

type UncommittedResolution = Readonly<{
  match: LiveMatch | null;
  persistence: MatchPersistenceState;
  durableMatch: LiveMatch | null;
  durableVersion: MatchVersion | null;
  durableVersionKnown: boolean;
}>;

type PendingCommit = Readonly<{
  write: MatchWrite;
  expected: MatchVersion | null;
  attempted: MatchVersion | null;
  prior: LiveMatch | null;
  next: LiveMatch | null;
  presentation: MatchTransitionPresentation | null;
  undoPresentations?: readonly MatchTransitionPresentation[];
  uncommitted: UncommittedResolution;
  history: MatchHistoryMutation | null;
}>;

type ConflictContext = Readonly<{
  pending: PendingCommit;
  displayed: LiveMatch | null;
  origin: "storage" | "ownership";
}>;

const DEFAULT_SELECTION: MatchSetupSelection = Object.freeze({
  mode: "bot",
  humanPlayer: "black",
  difficulty: "medium",
});

const EMPTY_PRESENTATION: MatchPresentation = Object.freeze({
  activeTransition: null,
  lastTransition: null,
  focusTarget: Object.freeze({ kind: "setup-primary" }),
  announcement: null,
});

const HEALTHY: MatchPersistenceState = Object.freeze({ kind: "healthy" });

const OK: MatchCommandResult = Object.freeze({ ok: true });

const LOCAL_OWNERSHIP: MatchOwnershipSnapshot = Object.freeze({
  kind: "owner",
  reason: "available",
  ownerId: "controller-local",
});

const localOwnershipPort: MatchOwnershipPort = Object.freeze({
  getSnapshot: () => LOCAL_OWNERSHIP,
  subscribe: () => () => undefined,
  acquire: () => Promise.resolve(LOCAL_OWNERSHIP),
  takeOver: () => Promise.resolve(LOCAL_OWNERSHIP),
  release: () =>
    Promise.resolve(Object.freeze({ kind: "read-only", reason: "available", ownerId: null })),
  dispose: () => Promise.resolve(),
});

export function routeTurn(
  game: GameState,
  result: MatchResult | null,
  humanPlayer: Player,
  mode: MatchMode = "bot",
): MatchRoute {
  if (result !== null || game.outcome !== null) return "finished";
  if (mode === "local") return "human-turn";
  return game.activePlayer === humanPlayer ? "human-turn" : "bot-thinking";
}

function defaultEpochFactory(): string {
  return globalThis.crypto.randomUUID();
}

function rejected(reason: Exclude<MatchCommandResult, { ok: true }>["reason"]): MatchCommandResult {
  return Object.freeze({ ok: false, reason });
}

function cloneSelection(selection: MatchSetupSelection): MatchSetupSelection {
  return Object.freeze({
    mode: selection.mode ?? "bot",
    humanPlayer: selection.humanPlayer,
    difficulty: selection.difficulty,
  });
}

function cloneVersion(version: MatchVersion | null): MatchVersion | null {
  return version === null
    ? null
    : Object.freeze({ epoch: version.epoch, revision: version.revision });
}

function cloneOwnership(snapshot: MatchOwnershipSnapshot): MatchOwnershipSnapshot {
  return Object.freeze({
    kind: snapshot.kind,
    reason: snapshot.reason,
    ownerId: snapshot.ownerId,
  });
}

function cloneBotDecision(decision: BotDecision, move: Move): BotDecision {
  return Object.freeze({
    move: Object.freeze({
      kind: move.kind,
      from: coordinate(move.from.row, move.from.column),
      to: coordinate(move.to.row, move.to.column),
    }),
    source: decision.source,
    ...(decision.fallbackReason === undefined ? {} : { fallbackReason: decision.fallbackReason }),
    ...(decision.nodes === undefined ? {} : { nodes: decision.nodes }),
    ...(decision.completedDepth === undefined ? {} : { completedDepth: decision.completedDepth }),
  });
}

function versionMatches(left: MatchVersion | null, right: MatchVersion | null): boolean {
  if (left === null || right === null) return left === right;
  return sameMatchVersion(left, right);
}

function loadVersion(result: Extract<LoadMatchResult, { kind: "loaded" }>): MatchVersion | null {
  return result.expected;
}

function resultFromSaved(saved: SavedMatchV3): MatchResult | null {
  return saved.controller.route === "finished" ? saved.controller.result : null;
}

function liveFromSaved(saved: SavedMatchV3): LiveMatch {
  const rulesVersion = saved.recording?.rulesVersion;
  const parsed =
    rulesVersion === LEGACY_MATCH_RULES_VERSION
      ? parseLegacyGame(saved.game)
      : rulesVersion === PRE_REPETITION_MATCH_RULES_VERSION
        ? parseVersion2Game(saved.game)
        : rulesVersion === CLEANUP_PASSAGE_MATCH_RULES_VERSION
          ? parseVersion3Game(saved.game)
          : parseGame(saved.game);
  if (!parsed.ok) {
    throw new TypeError(`Validated match contained an invalid game: ${parsed.error.message}`);
  }
  const replay = saved.recording === null ? null : replayRecording(saved.recording);
  return Object.freeze({
    saved,
    game: parsed.state,
    result: resultFromSaved(saved),
    positionKeys: replay?.positionKeys ?? Object.freeze([positionKey(parsed.state)]),
  });
}

function focusForTransition(move: Move, result: MatchResult | null): MatchFocusTarget {
  if (result !== null) return Object.freeze({ kind: "result-primary" });
  if (move.kind === "normal") {
    return Object.freeze({ kind: "field", position: coordinate(move.to.row, move.to.column) });
  }
  return Object.freeze({ kind: "board-heading" });
}

function committedVersionMatches(result: CommitResult, pending: PendingCommit): boolean {
  if (result.kind !== "committed") return false;
  if (pending.next === null) return versionMatches(result.version, pending.attempted);
  const expected = versionOf(pending.next.saved);
  return versionMatches(result.version, expected);
}

function tombstoneSuccessor(version: MatchVersion): MatchVersion {
  return Object.freeze({ epoch: version.epoch, revision: version.revision + 1 });
}

function storageFailure(code: StorageFailure["code"], message: string): StorageFailure {
  return Object.freeze({ code, message });
}

function freezePersistence(state: MatchPersistenceState): MatchPersistenceState {
  switch (state.kind) {
    case "healthy":
      return HEALTHY;
    case "degraded":
      return Object.freeze({
        ...state,
        reason: Object.freeze({ ...state.reason }),
        durableVersion: cloneVersion(state.durableVersion),
      });
    case "conflict":
      return Object.freeze({
        ...state,
        current: cloneVersion(state.current),
        attempted: cloneVersion(state.attempted),
        loadIssue: state.loadIssue === null ? null : Object.freeze({ ...state.loadIssue }),
      });
    case "reconciling":
      return Object.freeze({
        ...state,
        attempted: cloneVersion(state.attempted),
        issue: state.issue === null ? null : Object.freeze({ ...state.issue }),
      });
    case "invalid-save":
      return Object.freeze({ ...state, error: Object.freeze({ ...state.error }) });
  }
}

/**
 * Coordinates exactly one local hot-seat or bot match. Rules, worker search,
 * persistence and animation stay behind injected narrow ports.
 */
export class MatchController {
  readonly #bot: BotPort;
  readonly #animation: AnimationPort;
  readonly #storage: StoragePort;
  readonly #ownership: MatchOwnershipPort;
  readonly #epochFactory: MatchEpochFactory;
  readonly #historyIdFactory: MatchEpochFactory;
  readonly #clock: MatchClock;
  readonly #seedFactory: MatchSeedFactory;
  readonly #listeners = new Set<Listener>();
  readonly #unsubscribeOwnership: () => void;
  readonly #unsubscribeStorage: () => void;

  #phase: MatchPhase = "setup";
  #match: LiveMatch | null = null;
  #selection: MatchSetupSelection;
  #selectedPosition: Coordinate | null = null;
  #persistence: MatchPersistenceState = HEALTHY;
  #durableMatch: LiveMatch | null = null;
  #durableVersion: MatchVersion | null = null;
  #durableVersionKnown = true;
  #presentation: MatchPresentation = EMPTY_PRESENTATION;
  #pauseReason: MatchPauseReason | null = null;
  #pauseAfterOperation: MatchPauseReason | null = null;
  #hidden = false;
  #conflict: ConflictContext | null = null;
  #reconciliation: PendingCommit | null = null;
  #operationToken = 0;
  #restoreOperation: Promise<boolean> | null = null;
  #announcementSequence = 0;
  #disposed = false;
  #ownershipSnapshot: MatchOwnershipSnapshot;
  #snapshot: MatchControllerSnapshot;
  #history: readonly MatchHistoryFrame[] = Object.freeze([]);

  constructor(options: MatchControllerOptions) {
    this.#bot = options.bot;
    this.#animation = options.animation;
    this.#storage = options.storage;
    this.#ownership = options.ownership ?? localOwnershipPort;
    this.#epochFactory = options.epochFactory ?? defaultEpochFactory;
    this.#historyIdFactory = options.historyIdFactory ?? defaultEpochFactory;
    this.#clock = options.clock ?? Date.now;
    this.#seedFactory = options.seedFactory ?? deriveBotSeed;
    this.#selection = cloneSelection(options.initialSelection ?? DEFAULT_SELECTION);
    this.#ownershipSnapshot = cloneOwnership(this.#ownership.getSnapshot());
    this.#snapshot = this.#buildSnapshot();
    this.#unsubscribeOwnership = this.#ownership.subscribe((snapshot) => {
      this.#onOwnershipChanged(snapshot);
    });
    this.#unsubscribeStorage =
      this.#storage.subscribe?.(() => {
        if (this.#persistence.kind === "conflict" && !this.#disposed) {
          void this.reloadConflict();
        }
      }) ?? (() => undefined);
  }

  readonly getSnapshot = (): MatchControllerSnapshot => this.#snapshot;

  readonly subscribe = (listener: Listener): (() => void) => {
    if (this.#disposed) return () => undefined;
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  async restore(): Promise<MatchCommandResult> {
    if (this.#disposed) return rejected("disposed");
    if (this.#phase !== "setup") return rejected("wrong-phase");

    this.#cancelTransientWork();
    const token = ++this.#operationToken;
    this.#phase = "restoring";
    this.#pauseReason = null;
    this.#emit();

    const operation = (async (): Promise<boolean> => {
      await this.#acquireOwnership();
      if (!this.#isCurrent(token)) return false;
      const result = await this.#loadMatch();
      if (!this.#isCurrent(token)) return false;
      const repaired = await this.#repairUnrecordedFinishedMatch(result);
      if (!this.#isCurrent(token)) return false;
      const upgraded = await this.#upgradeOutdatedActiveMatch(repaired);
      if (!this.#isCurrent(token)) return false;
      this.#applyRestoration(upgraded);
      return true;
    })();
    this.#restoreOperation = operation;
    let applied: boolean;
    try {
      applied = await operation;
    } finally {
      if (this.#restoreOperation === operation) this.#restoreOperation = null;
    }
    return applied ? OK : rejected("wrong-phase");
  }

  async start(selection: MatchSetupSelection): Promise<MatchCommandResult> {
    if (this.#disposed) return rejected("disposed");
    if (this.#phase === "restoring" && this.#restoreOperation !== null) {
      await this.#restoreOperation;
      if (this.#snapshot.disposed) return rejected("disposed");
    }
    if (
      this.#phase === "setup" &&
      this.#ownershipSnapshot.kind === "read-only" &&
      this.#ownershipSnapshot.reason === "available"
    ) {
      const restored = await this.restore();
      if (!restored.ok) return restored;
    }
    if (this.#phase !== "setup") return rejected("wrong-phase");
    if (this.#ownershipBlocksMutation()) return rejected("not-owner");
    this.#ensureOwnershipDegraded();
    this.#history = this.#emptyHistory();

    this.#selection = cloneSelection(selection);
    const initialGame = createInitialGame();
    const next = this.#createLiveMatch({
      epoch: this.#nextEpoch(),
      revision: 0,
      game: initialGame,
      selection: this.#selection,
      result: null,
      recording: createMatchRecording(this.#historyIdFactory(), initialGame),
    });

    if (this.#persistence.kind === "degraded") {
      this.#match = next;
      this.#selectedPosition = null;
      this.#persistence = Object.freeze({ ...this.#persistence, canDiscard: true });
      this.#routeCurrent();
      return OK;
    }

    const pending = this.#replacementPending(
      next,
      this.#durableVersionKnown ? this.#durableVersion : null,
    );
    await this.#commitPending(pending);
    return OK;
  }

  async continueFrom(game: GameState, selection: MatchSetupSelection): Promise<MatchCommandResult> {
    if (this.#disposed) return rejected("disposed");
    if (
      this.#phase === "restoring" ||
      this.#phase === "committing" ||
      this.#phase === "reconciling" ||
      (this.#phase === "paused" && this.#pauseReason === "conflict")
    ) {
      return rejected("wrong-phase");
    }
    if (game.outcome !== null) return rejected("terminal-position");
    if (!validateGameState(game).ok) return rejected("incompatible-position");
    if (this.#ownershipBlocksMutation()) return rejected("not-owner");

    this.#ensureOwnershipDegraded();
    this.#cancelTransientWork();
    const prior = this.#match;
    this.#selection = cloneSelection(selection);
    this.#history = this.#emptyHistory();
    const next = this.#createLiveMatch({
      epoch: this.#nextEpoch(),
      revision: 0,
      game,
      selection: this.#selection,
      result: null,
      recording: createMatchRecording(this.#historyIdFactory(), game),
    });

    if (this.#persistence.kind === "degraded") {
      this.#match = next;
      this.#selectedPosition = null;
      this.#persistence = Object.freeze({ ...this.#persistence, canDiscard: true });
      this.#routeCurrent();
      return OK;
    }

    const expected =
      prior === null
        ? this.#durableVersionKnown
          ? this.#durableVersion
          : null
        : versionOf(prior.saved);
    const pending: PendingCommit = Object.freeze({
      write: Object.freeze({ kind: "replace", expected, next: next.saved }),
      expected,
      attempted: versionOf(next.saved),
      prior,
      next,
      presentation: null,
      uncommitted: this.#uncommittedResolution(prior, HEALTHY),
      history: { kind: "clear" },
    });
    await this.#commitPending(pending);
    return OK;
  }

  selectSource(position: Coordinate): MatchCommandResult {
    if (this.#disposed) return rejected("disposed");
    if (this.#phase !== "human-turn" || this.#match === null) {
      return rejected("not-human-turn");
    }

    const legalFrom = this.#legalMoves(this.#match).filter(
      (move) => move.from.row === position.row && move.from.column === position.column,
    );
    const cell = cellAt(this.#match.game, position);
    if (
      legalFrom.length === 0 ||
      cell?.kind !== "field" ||
      cell.stack.at(-1) !== this.#match.game.activePlayer
    ) {
      return rejected("illegal-move");
    }

    this.#selectedPosition = coordinate(position.row, position.column);
    this.#presentation = Object.freeze({
      ...this.#presentation,
      announcement: this.#announcement({
        kind: "selection",
        position: this.#selectedPosition,
        stack: Object.freeze([...cell.stack]),
        legalTargetCount: legalFrom.length,
      }),
    });
    this.#emit();
    return OK;
  }

  clearSelection(): MatchCommandResult {
    if (this.#disposed) return rejected("disposed");
    if (this.#phase !== "human-turn") return rejected("not-human-turn");
    if (this.#selectedPosition !== null) {
      this.#selectedPosition = null;
      this.#emit();
    }
    return OK;
  }

  async playHumanMove(move: Move): Promise<MatchCommandResult> {
    if (this.#disposed) return rejected("disposed");
    if (this.#phase !== "human-turn" || this.#match === null) {
      return rejected("not-human-turn");
    }
    if (this.#ownershipBlocksMutation()) return rejected("not-owner");
    this.#ensureOwnershipDegraded();
    const canonical = this.#canonicalMove(this.#match, move);
    if (canonical === null) return rejected("illegal-move");

    this.#selectedPosition = null;
    const actor: MatchActor =
      this.#match.saved.mode === "local" ? this.#match.game.activePlayer : "human";
    await this.#applyMoveAndCommit(this.#match, canonical, actor, null);
    return OK;
  }

  async confirmDraw(): Promise<MatchCommandResult> {
    if (this.#disposed) return rejected("disposed");
    if (!this.#canEndAsDraw() || this.#match === null) return rejected("wrong-phase");
    if (this.#ownershipBlocksMutation()) return rejected("not-owner");
    this.#ensureOwnershipDegraded();

    this.#cancelTransientWork();
    const prior = this.#match;
    const next = this.#advancedLiveMatch(
      prior,
      prior.game,
      { kind: "manual-draw" },
      completeRecording(prior.saved.recording, this.#clock()),
    );
    const pending = this.#advancePending(prior, next, null, null);
    await this.#commitPending(pending);
    return OK;
  }

  async undoLastMove(): Promise<MatchCommandResult> {
    if (this.#disposed) return rejected("disposed");
    if (this.#phase === "setup" || this.#phase === "restoring" || this.#match === null) {
      return rejected("no-match");
    }
    if (this.#ownershipBlocksMutation()) return rejected("not-owner");
    const depth = this.#undoDepth(this.#match);
    if (!this.#canUndoForDepth(depth)) return rejected("no-history");

    const target = this.#undoTarget(depth);
    if (target === null) return rejected("no-history");

    this.#cancelTransientWork();
    this.#ensureOwnershipDegraded();
    const prior = this.#match;
    const next = target;
    const undoPresentations = Object.freeze(this.#buildUndoPresentations(prior, next, depth));
    const pending: PendingCommit = Object.freeze({
      write: Object.freeze({
        kind: "replace",
        expected: versionOf(prior.saved),
        next: next.saved,
      }),
      expected: versionOf(prior.saved),
      attempted: versionOf(next.saved),
      prior,
      next,
      presentation: null,
      undoPresentations,
      uncommitted: this.#uncommittedResolution(prior, this.#persistence),
      history: {
        kind: "restore",
        history: Object.freeze(this.#history.slice(0, this.#history.length - depth)),
      },
    });
    await this.#commitPending(pending);
    return OK;
  }

  pause(reason: Exclude<MatchPauseReason, "conflict"> = "user"): MatchCommandResult {
    if (this.#disposed) return rejected("disposed");
    if (this.#phase === "committing") {
      if (this.#pauseAfterOperation !== "user") this.#pauseAfterOperation = reason;
      this.#cancelWorkerAndAnimation(false);
      this.#emit();
      return OK;
    }
    if (this.#phase === "reconciling" || this.#phase === "paused") {
      return rejected("wrong-phase");
    }
    if (this.#phase === "setup" || this.#phase === "restoring" || this.#phase === "finished") {
      return rejected("wrong-phase");
    }

    this.#cancelTransientWork();
    this.#finishInterruptedPresentation();
    this.#phase = "paused";
    this.#pauseReason = reason;
    this.#emit();
    return OK;
  }

  resume(): MatchCommandResult {
    if (this.#disposed) return rejected("disposed");
    if (this.#phase === "committing" && this.#pauseAfterOperation === "user") {
      this.#pauseAfterOperation = null;
      this.#emit();
      return OK;
    }
    if (this.#phase !== "paused" || this.#pauseReason === "conflict") {
      return rejected("wrong-phase");
    }
    this.#pauseReason = null;
    this.#routeCurrent();
    return OK;
  }

  setHidden(hidden: boolean): MatchCommandResult {
    if (this.#disposed) return rejected("disposed");
    this.#hidden = hidden;
    if (hidden) {
      const storageOperation =
        this.#phase === "restoring" ||
        this.#phase === "committing" ||
        this.#phase === "reconciling";
      this.#cancelWorkerAndAnimation(!storageOperation);
      if (this.#phase === "committing" || this.#phase === "reconciling") {
        if (this.#pauseAfterOperation !== "user") this.#pauseAfterOperation = "hidden";
        this.#emit();
        return OK;
      }
      if (
        this.#phase === "human-turn" ||
        this.#phase === "bot-thinking" ||
        this.#phase === "animating"
      ) {
        this.#finishInterruptedPresentation();
        this.#phase = "paused";
        this.#pauseReason = "hidden";
        this.#emit();
      }
      return OK;
    }

    if (
      (this.#phase === "committing" || this.#phase === "reconciling") &&
      this.#pauseAfterOperation === "hidden"
    ) {
      this.#pauseAfterOperation = null;
      this.#emit();
      return OK;
    }
    if (this.#phase === "paused" && this.#pauseReason === "hidden") return this.resume();
    return OK;
  }

  async restart(): Promise<MatchCommandResult> {
    if (this.#disposed) return rejected("disposed");
    if (this.#match === null) return rejected("no-match");
    if (this.#phase === "committing" || this.#phase === "reconciling") {
      return rejected("wrong-phase");
    }
    if (this.#phase === "paused" && this.#pauseReason === "conflict") {
      return rejected("wrong-phase");
    }
    if (this.#ownershipBlocksMutation()) return rejected("not-owner");
    this.#ensureOwnershipDegraded();

    this.#cancelTransientWork();
    const prior = this.#match;
    if (this.#persistence.kind === "degraded") {
      this.#discardLocalToSetup();
      return OK;
    }

    const expected = versionOf(prior.saved);
    const pending: PendingCommit = Object.freeze({
      write: Object.freeze({
        kind: "tombstone",
        expected,
        archive: this.#recordForRestart(prior),
      }),
      expected,
      attempted: tombstoneSuccessor(expected),
      prior,
      next: null,
      presentation: null,
      uncommitted: this.#uncommittedResolution(prior, HEALTHY),
      history: { kind: "clear" },
    });
    await this.#commitPending(pending);
    return OK;
  }

  async rematch(): Promise<MatchCommandResult> {
    if (this.#phase !== "finished") return rejected("wrong-phase");
    return this.restart();
  }

  async retryStorage(): Promise<MatchCommandResult> {
    if (this.#disposed) return rejected("disposed");
    if (this.#persistence.kind === "invalid-save") return this.restoreInvalidSave();
    if (this.#persistence.kind !== "degraded") return rejected("not-degraded");
    if (this.#phase === "committing" || this.#phase === "reconciling") {
      return rejected("wrong-phase");
    }

    this.#cancelTransientWork();
    const ownershipToken = ++this.#operationToken;
    this.#phase = "restoring";
    this.#pauseReason = null;
    this.#emit();
    await this.#acquireOwnership();
    if (!this.#isCurrent(ownershipToken)) {
      return rejected("wrong-phase");
    }
    if (this.#ownershipSnapshot.kind !== "owner") {
      this.#routeOrSetup();
      return rejected("not-owner");
    }

    if (this.#match === null) return this.#retryStorageWithoutMatch();

    const local = this.#match;
    const next = this.#createLiveMatch({
      epoch: this.#nextEpoch(),
      revision: 0,
      game: local.game,
      selection: this.#selection,
      result: local.result,
      recording: local.saved.recording,
    });
    const expected = this.#durableVersionKnown ? this.#durableVersion : null;
    const previousPersistence = this.#persistence;
    const pending: PendingCommit = Object.freeze({
      write: Object.freeze({ kind: "replace", expected, next: next.saved }),
      expected,
      attempted: versionOf(next.saved),
      prior: local,
      next,
      presentation: null,
      uncommitted: this.#uncommittedResolution(local, previousPersistence),
      history: null,
    });
    await this.#commitPending(pending, true);
    return OK;
  }

  discardVolatileMatch(): MatchCommandResult {
    if (this.#disposed) return rejected("disposed");
    if (this.#persistence.kind !== "degraded" && this.#persistence.kind !== "invalid-save") {
      return rejected("not-degraded");
    }
    this.#cancelTransientWork();
    this.#discardLocalToSetup();
    return OK;
  }

  async retryReconciliation(): Promise<MatchCommandResult> {
    if (this.#disposed) return rejected("disposed");
    if (this.#phase !== "reconciling" || this.#reconciliation === null) {
      return rejected("not-reconciling");
    }
    const token = ++this.#operationToken;
    await this.#runReconciliation(this.#reconciliation, token);
    return OK;
  }

  abandonReconciliation(): MatchCommandResult {
    if (this.#disposed) return rejected("disposed");
    if (this.#phase !== "reconciling" || this.#reconciliation === null) {
      return rejected("not-reconciling");
    }

    this.#cancelTransientWork();
    this.#history = this.#emptyHistory();
    this.#match = null;
    this.#reconciliation = null;
    this.#conflict = null;
    this.#durableMatch = null;
    this.#durableVersion = null;
    this.#durableVersionKnown = false;
    this.#phase = "setup";
    this.#pauseReason = null;
    this.#persistence = this.#degradedPersistence(
      storageFailure(
        "watchdog-expired",
        "The unresolved saved match was abandoned locally; its durable state remains unknown.",
      ),
      null,
      false,
    );
    this.#presentation = this.#setupPresentation();
    this.#emit();
    return OK;
  }

  async reloadConflict(): Promise<MatchCommandResult> {
    if (this.#disposed) return rejected("disposed");
    if (this.#persistence.kind !== "conflict" || this.#conflict === null) {
      return rejected("not-conflicted");
    }
    this.#history = this.#emptyHistory();
    const token = ++this.#operationToken;
    const loaded = await this.#loadMatch();
    if (!this.#isCurrent(token)) return rejected("wrong-phase");

    if (loaded.kind === "loaded") {
      const displayed = loaded.match === null ? null : liveFromSaved(loaded.match);
      if (displayed === null) {
        this.#match = null;
        this.#durableMatch = null;
        this.#durableVersion = loaded.expected;
        this.#durableVersionKnown = true;
        this.#conflict = null;
        this.#persistence = HEALTHY;
        this.#phase = "setup";
        this.#pauseReason = null;
        this.#presentation = this.#setupPresentation();
      } else {
        this.#match = displayed;
        this.#durableMatch = displayed;
        this.#durableVersion = loaded.expected;
        this.#durableVersionKnown = true;
        this.#conflict = Object.freeze({ ...this.#conflict, displayed });
        this.#persistence = this.#conflictPersistence(
          this.#durableVersion,
          this.#conflict.pending.next === null
            ? null
            : versionOf(this.#conflict.pending.next.saved),
          null,
        );
      }
    } else {
      const issue = loaded.kind === "invalid" ? loaded.error : loaded.reason;
      this.#persistence = this.#conflictPersistence(
        this.#persistence.current,
        this.#persistence.attempted,
        issue,
      );
    }
    this.#emit();
    return OK;
  }

  async takeOver(): Promise<MatchCommandResult> {
    if (this.#disposed) return rejected("disposed");
    const conflicted = this.#conflict;
    const ownershipOnly = this.#ownershipSnapshot.kind === "read-only";
    if ((this.#persistence.kind !== "conflict" || conflicted === null) && !ownershipOnly) {
      return rejected("not-conflicted");
    }

    this.#cancelTransientWork();
    this.#phase = "restoring";
    this.#pauseReason = null;
    this.#emit();
    const token = ++this.#operationToken;
    const ownership = await this.#takeOwnership();
    if (!this.#isCurrent(token)) return rejected("wrong-phase");
    if (ownership.kind !== "owner") {
      if (this.#match === null) {
        this.#phase = "setup";
        this.#pauseReason = null;
      } else {
        this.#phase = "paused";
        this.#pauseReason = "conflict";
      }
      this.#emit();
      return rejected("not-owner");
    }

    const loaded = await this.#loadMatch();
    if (!this.#isCurrent(token)) return rejected("wrong-phase");
    if (
      this.#ownershipSnapshot.kind !== "owner" ||
      this.#ownershipSnapshot.ownerId !== ownership.ownerId
    ) {
      this.#routeOrSetup();
      return rejected("not-owner");
    }
    if (loaded.kind !== "loaded") {
      if (conflicted === null || this.#persistence.kind !== "conflict") {
        this.#applyRestoration(loaded);
      } else {
        const issue = loaded.kind === "invalid" ? loaded.error : loaded.reason;
        this.#persistence = this.#conflictPersistence(
          this.#persistence.current,
          this.#persistence.attempted,
          issue,
        );
        this.#emit();
      }
      return OK;
    }

    const external = loaded.match === null ? null : liveFromSaved(loaded.match);
    const current = loaded.expected;
    // A focused tab continues from the latest durable position. Ownership handoff is not a
    // branch-selection surface and must never resurrect the follower's stale snapshot.
    const candidate = external;

    if (candidate === null) {
      if (external === null) {
        this.#match = null;
        this.#durableMatch = null;
        this.#durableVersion = current;
        this.#durableVersionKnown = true;
        this.#conflict = null;
        this.#persistence = HEALTHY;
        this.#phase = "setup";
        this.#pauseReason = null;
        this.#presentation = this.#setupPresentation();
        this.#emit();
        return OK;
      }
      if (current === null) {
        throw new Error("A loaded live match did not expose a CAS token.");
      }
      const pending: PendingCommit = Object.freeze({
        write: Object.freeze({ kind: "tombstone", expected: current }),
        expected: current,
        attempted: tombstoneSuccessor(current),
        prior: external,
        next: null,
        presentation: null,
        uncommitted: this.#uncommittedResolution(
          external,
          this.#conflictPersistence(current, null, null),
        ),
        history: { kind: "clear" },
      });
      await this.#commitPending(pending, true);
      return OK;
    }

    const next = this.#createLiveMatch({
      epoch: this.#nextEpoch(),
      revision: 0,
      game: candidate.game,
      selection: {
        mode: candidate.saved.mode,
        humanPlayer: candidate.saved.humanPlayer,
        difficulty: candidate.saved.bot.difficulty,
      },
      result: candidate.result,
      recording: candidate.saved.recording,
    });
    const pending: PendingCommit = Object.freeze({
      write: Object.freeze({ kind: "replace", expected: current, next: next.saved }),
      expected: current,
      attempted: versionOf(next.saved),
      prior: external,
      next,
      presentation: null,
      uncommitted: this.#uncommittedResolution(
        external,
        this.#conflictPersistence(current, versionOf(next.saved), null),
      ),
      history: { kind: "clear" },
    });
    await this.#commitPending(pending, true);
    return OK;
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#operationToken += 1;
    this.#unsubscribeOwnership();
    this.#unsubscribeStorage();
    this.#ownershipSnapshot = Object.freeze({
      kind: "read-only",
      reason: "disposed",
      ownerId: null,
    });
    this.#bot.dispose();
    this.#animation.cancel();
    this.#animation.dispose?.();
    void this.#ownership.dispose().catch(() => undefined);
    this.#storage.dispose?.();
    this.#selectedPosition = null;
    this.#snapshot = this.#buildSnapshot();
    for (const listener of this.#listeners) listener();
    this.#listeners.clear();
  }

  private async restoreInvalidSave(): Promise<MatchCommandResult> {
    const token = ++this.#operationToken;
    this.#phase = "restoring";
    this.#emit();
    const result = await this.#loadMatch();
    if (!this.#isCurrent(token)) return rejected(this.#disposed ? "disposed" : "wrong-phase");
    this.#applyRestoration(result);
    return OK;
  }

  async #repairUnrecordedFinishedMatch(result: LoadMatchResult): Promise<LoadMatchResult> {
    if (
      result.kind !== "loaded" ||
      result.match?.recording !== null ||
      result.match.controller.route !== "finished" ||
      result.expected === null ||
      this.#ownershipSnapshot.kind !== "owner"
    ) {
      return result;
    }

    const parsed = parseGame(result.match.game);
    if (!parsed.ok) return result;
    const recording = completeRecording(
      createMatchRecording(result.match.epoch, parsed.state),
      this.#clock(),
    );
    if (recording === null) throw new Error("Recovered recording unexpectedly remained null.");
    const rotate = result.match.revision >= MAX_SAVED_MATCH_REVISION;
    const repaired = createSavedMatch({
      epoch: rotate ? this.#epochFactory() : result.match.epoch,
      revision: rotate ? 0 : result.match.revision + 1,
      game: parsed.state,
      mode: result.match.mode,
      humanPlayer: result.match.humanPlayer,
      difficulty: result.match.bot.difficulty,
      controllerResult: result.match.controller.result,
      recording,
    });
    const write: MatchWrite = rotate
      ? Object.freeze({ kind: "replace", expected: result.expected, next: repaired })
      : Object.freeze({ kind: "advance", expected: result.expected, next: repaired });
    const committed = await this.#storage.commitMatch(write);
    if (
      committed.kind === "committed" &&
      committed.version !== null &&
      sameMatchVersion(committed.version, versionOf(repaired))
    ) {
      return Object.freeze({ kind: "loaded", match: repaired, expected: versionOf(repaired) });
    }
    if (committed.kind === "conflict" || committed.kind === "indeterminate") {
      const observed = await this.#loadMatch();
      if (observed.kind === "loaded") return observed;
    }
    return result;
  }

  async #upgradeOutdatedActiveMatch(result: LoadMatchResult): Promise<LoadMatchResult> {
    if (
      result.kind !== "loaded" ||
      result.match === null ||
      result.expected === null ||
      this.#ownershipSnapshot.kind !== "owner"
    ) {
      return result;
    }
    const saved = result.match;
    if (
      saved.recording === null ||
      saved.recording.rulesVersion === MATCH_RULES_VERSION ||
      saved.controller.route !== "active"
    ) {
      return result;
    }

    const legacy = liveFromSaved(saved);
    const validation = validateGameState(legacy.game);
    const missingOutcome =
      !validation.ok &&
      validation.issues.length === 1 &&
      validation.issues[0]?.code === "missing-terminal-outcome";
    if (!validation.ok && !missingOutcome) return result;

    const outcome: MatchResult | null = missingOutcome
      ? Object.freeze({
          kind: "no-moves",
          winner: opponentOf(legacy.game.activePlayer),
          loser: legacy.game.activePlayer,
        })
      : null;
    const game = validation.ok
      ? validation.state
      : createGameState({
          cells: legacy.game.cells,
          activePlayer: legacy.game.activePlayer,
          outcome,
        });
    const partial = createMatchRecording(saved.recording.id, game);
    const recording = outcome === null ? partial : completeRecording(partial, this.#clock());
    if (recording === null) throw new Error("Upgraded recording unexpectedly remained null.");

    const rotate = saved.revision >= MAX_SAVED_MATCH_REVISION;
    const upgraded = createSavedMatch({
      epoch: rotate ? this.#epochFactory() : saved.epoch,
      revision: rotate ? 0 : saved.revision + 1,
      game,
      mode: saved.mode,
      humanPlayer: saved.humanPlayer,
      difficulty: saved.bot.difficulty,
      controllerResult: outcome,
      recording,
    });
    const write: MatchWrite = rotate
      ? Object.freeze({ kind: "replace", expected: result.expected, next: upgraded })
      : Object.freeze({ kind: "advance", expected: result.expected, next: upgraded });
    const committed = await this.#storage.commitMatch(write);
    if (
      committed.kind === "committed" &&
      committed.version !== null &&
      sameMatchVersion(committed.version, versionOf(upgraded))
    ) {
      return Object.freeze({ kind: "loaded", match: upgraded, expected: versionOf(upgraded) });
    }
    if (committed.kind === "conflict" || committed.kind === "indeterminate") {
      const observed = await this.#loadMatch();
      if (
        observed.kind !== "loaded" ||
        observed.match?.controller.route !== "active" ||
        observed.match.recording?.rulesVersion === MATCH_RULES_VERSION
      ) {
        return observed;
      }
    }
    const reason =
      committed.kind === "unavailable" || committed.kind === "indeterminate"
        ? committed.reason
        : storageFailure(
            "write-failed",
            "The saved match could not be upgraded to the current rules safely.",
          );
    return Object.freeze({ kind: "unavailable", reason });
  }

  async #retryStorageWithoutMatch(): Promise<MatchCommandResult> {
    this.#history = this.#emptyHistory();
    const token = ++this.#operationToken;
    this.#phase = "restoring";
    this.#emit();
    const loaded = await this.#loadMatch();
    if (!this.#isCurrent(token)) return rejected(this.#disposed ? "disposed" : "wrong-phase");

    if (loaded.kind === "loaded" && loaded.match === null) {
      this.#persistence = HEALTHY;
      this.#durableMatch = null;
      this.#durableVersion = loaded.expected;
      this.#durableVersionKnown = true;
      this.#phase = "setup";
      this.#emit();
      return OK;
    }
    if (loaded.kind === "loaded") {
      if (loaded.match === null) throw new Error("Loaded match narrowing failed.");
      const external = liveFromSaved(loaded.match);
      const placeholder = this.#tombstoneConflictPending(external);
      this.#enterConflict(placeholder, loaded.expected, external);
      return OK;
    }
    if (loaded.kind === "invalid") {
      this.#persistence = Object.freeze({
        kind: "invalid-save",
        error: loaded.error,
        canRetry: true,
        canDiscard: true,
      });
      this.#phase = "setup";
      this.#emit();
      return OK;
    }

    this.#persistence = this.#degradedPersistence(
      loaded.reason,
      this.#durableVersion,
      this.#durableVersionKnown,
    );
    this.#phase = "setup";
    this.#emit();
    return OK;
  }

  #applyRestoration(result: LoadMatchResult): void {
    this.#history = this.#emptyHistory();
    this.#selectedPosition = null;
    this.#pauseReason = null;
    this.#pauseAfterOperation = null;
    this.#conflict = null;
    this.#reconciliation = null;
    this.#presentation = EMPTY_PRESENTATION;

    if (result.kind === "loaded") {
      const match = result.match === null ? null : liveFromSaved(result.match);
      this.#match = match;
      this.#durableMatch = match;
      this.#durableVersion = result.expected;
      this.#durableVersionKnown = true;
      this.#persistence = HEALTHY;
      this.#ensureOwnershipDegraded();
      if (match === null) {
        this.#phase = "setup";
        this.#presentation = this.#setupPresentation();
      } else {
        this.#selection = cloneSelection({
          mode: match.saved.mode,
          humanPlayer: match.saved.humanPlayer,
          difficulty: match.saved.bot.difficulty,
        });
        if (this.#ownershipBlocksMutation()) {
          this.#enterOwnershipConflict(match);
          return;
        }
        this.#routeCurrent();
      }
      this.#emitIfRouteDidNot();
      return;
    }

    this.#match = null;
    this.#durableMatch = null;
    this.#durableVersion = null;
    this.#phase = "setup";
    this.#presentation = this.#setupPresentation();
    if (result.kind === "invalid") {
      this.#durableVersionKnown = false;
      this.#persistence = Object.freeze({
        kind: "invalid-save",
        error: result.error,
        canRetry: true,
        canDiscard: true,
      });
    } else {
      this.#durableVersionKnown = false;
      this.#persistence = this.#degradedPersistence(result.reason, null, false);
    }
    this.#emit();
  }

  #emitIfRouteDidNot(): void {
    // routeCurrent always emits. Setup restoration does not route.
    if (this.#phase === "setup") this.#emit();
  }

  async #applyMoveAndCommit(
    prior: LiveMatch,
    move: Move,
    actor: MatchActor,
    botDecision: BotDecision | null,
  ): Promise<void> {
    const rulesVersion = prior.saved.recording?.rulesVersion;
    const transition = this.#applyRulesMove(prior, move);
    if (transition === null) {
      throw new Error(`A canonical ${actor} recorded move was rejected.`);
    }
    const movedRecording = appendRecordedMove(prior.saved.recording, move);
    const positionKeys =
      transition.state.outcome === null
        ? Object.freeze([...prior.positionKeys, positionKey(transition.state)])
        : prior.positionKeys;
    const result: MatchResult | null =
      transition.state.outcome ??
      (rulesVersion !== undefined &&
      usesRepetitionRules(rulesVersion) &&
      isThreefoldRepetition(positionKeys, transition.state)
        ? Object.freeze({ kind: "repetition-draw" })
        : null);
    const next = this.#advancedLiveMatch(
      prior,
      transition.state,
      result,
      result === null ? movedRecording : completeRecording(movedRecording, this.#clock()),
    );
    const authority = versionOf(next.saved);
    const presentation: MatchTransitionPresentation = Object.freeze({
      actor,
      move,
      previousGame: prior.game,
      transition,
      authority,
      botDecision: botDecision === null ? null : cloneBotDecision(botDecision, move),
      focusAfter: focusForTransition(move, result),
      playback: Object.freeze({ direction: "forward" }),
    });
    const pending = this.#advancePending(
      prior,
      next,
      presentation,
      Object.freeze({
        kind: "append",
        frame: Object.freeze({
          live: prior,
          presentation,
        }),
      }),
    );
    await this.#commitPending(pending);
  }

  #buildUndoPresentations(
    current: LiveMatch,
    _target: LiveMatch,
    depth: number,
  ): readonly MatchTransitionPresentation[] {
    const toUndo = this.#history.slice(this.#history.length - depth);
    const presentations: MatchTransitionPresentation[] = [];
    let source = current;
    for (let index = toUndo.length - 1; index >= 0; index -= 1) {
      const frame = toUndo[index];
      if (frame === undefined) continue;
      presentations.push(this.#reversePresentation(frame, source));
      source = frame.live;
    }
    return Object.freeze(presentations);
  }

  #reversePresentation(frame: MatchHistoryFrame, current: LiveMatch): MatchTransitionPresentation {
    const forward = frame.presentation;
    const restoredTransition = Object.freeze({
      state: frame.live.game,
      events: Object.freeze([]),
    });
    return Object.freeze({
      actor: forward.actor,
      move: forward.move,
      previousGame: current.game,
      transition: restoredTransition,
      authority: versionOf(frame.live.saved),
      botDecision: null,
      focusAfter: Object.freeze({ kind: "board-heading" }),
      playback: Object.freeze({
        direction: "reverse",
        forward: Object.freeze({
          previousGame: forward.previousGame,
          move: forward.move,
          transition: forward.transition,
        }),
      }),
    });
  }

  #reviewTimeline(): MatchReviewTimeline | null {
    const match = this.#match;
    if (match === null) return null;
    if (match.result === null || this.#history.length === 0) return null;
    const states = Object.freeze([...this.#history.map((frame) => frame.live.game), match.game]);
    const transitions = Object.freeze(
      this.#history.map((frame, index): MatchReviewTransition => {
        const current = this.#history[index + 1]?.live ?? match;
        return Object.freeze({
          forward: frame.presentation,
          reverse: this.#reversePresentation(frame, current),
        });
      }),
    );
    return Object.freeze({ states, transitions });
  }

  #advancePending(
    prior: LiveMatch,
    next: LiveMatch,
    presentation: MatchTransitionPresentation | null,
    history: MatchHistoryMutation | null = null,
  ): PendingCommit {
    const priorVersion = versionOf(prior.saved);
    const nextVersion = versionOf(next.saved);
    const write: MatchWrite =
      nextVersion.epoch === priorVersion.epoch
        ? Object.freeze({ kind: "advance", expected: priorVersion, next: next.saved })
        : Object.freeze({ kind: "replace", expected: priorVersion, next: next.saved });
    return Object.freeze({
      write,
      expected: priorVersion,
      attempted: nextVersion,
      prior,
      next,
      presentation,
      uncommitted: this.#uncommittedResolution(prior, HEALTHY),
      history,
    });
  }

  #replacementPending(
    next: LiveMatch,
    expected: MatchVersion | null,
    history: MatchHistoryMutation | null = null,
  ): PendingCommit {
    return Object.freeze({
      write: Object.freeze({ kind: "replace", expected, next: next.saved }),
      expected,
      attempted: versionOf(next.saved),
      prior: null,
      next,
      presentation: null,
      uncommitted: this.#uncommittedResolution(null, HEALTHY),
      history,
    });
  }

  async #commitPending(pending: PendingCommit, forceStorage = false): Promise<void> {
    this.#cancelWorkerAndAnimation(true);
    const token = ++this.#operationToken;
    this.#match = pending.next ?? pending.prior;
    this.#selectedPosition = null;
    this.#phase = "committing";
    this.#pauseReason = null;
    this.#conflict = null;
    this.#reconciliation = null;
    this.#presentation = Object.freeze({
      ...this.#presentation,
      activeTransition: pending.presentation,
      focusTarget: pending.presentation?.focusAfter ?? this.#presentation.focusTarget,
    });
    this.#emit();

    if (!forceStorage && this.#persistence.kind === "degraded") {
      await this.#completeCommittedPending(pending, token, false);
      return;
    }

    let result: CommitResult;
    try {
      result = await this.#storage.commitMatch(pending.write);
    } catch {
      result = Object.freeze({
        kind: "indeterminate",
        attempted: pending.attempted,
        reason: storageFailure(
          "write-failed",
          "Match storage failed without reporting whether the write committed.",
        ),
      });
    }
    if (!this.#isCurrent(token)) return;
    await this.#handleCommitResult(pending, result, token);
  }

  async #handleCommitResult(
    pending: PendingCommit,
    result: CommitResult,
    token: number,
  ): Promise<void> {
    if (result.kind === "committed" && committedVersionMatches(result, pending)) {
      this.#durableMatch = pending.next;
      this.#durableVersion = result.version;
      this.#durableVersionKnown = true;
      this.#persistence = HEALTHY;
      await this.#completeCommittedPending(pending, token, true);
      return;
    }

    if (result.kind === "unavailable") {
      this.#durableMatch = pending.uncommitted.durableMatch;
      this.#durableVersion = pending.expected;
      this.#durableVersionKnown =
        pending.uncommitted.durableVersionKnown &&
        versionMatches(pending.expected, pending.uncommitted.durableVersion);
      this.#persistence = this.#degradedPersistence(
        result.reason,
        this.#durableVersion,
        this.#durableVersionKnown,
      );
      await this.#completeCommittedPending(pending, token, false);
      return;
    }

    if (result.kind === "conflict") {
      this.#enterConflict(pending, result.current, pending.prior);
      return;
    }

    const reason =
      result.kind === "indeterminate"
        ? result.reason
        : storageFailure(
            "write-failed",
            "Storage reported a committed match version that did not match the attempted write.",
          );
    const reconcilingPending: PendingCommit = Object.freeze({
      ...pending,
      attempted: result.kind === "indeterminate" ? result.attempted : pending.attempted,
    });
    this.#reconciliation = reconcilingPending;
    this.#phase = "reconciling";
    this.#persistence = Object.freeze({
      kind: "reconciling",
      attempted: reconcilingPending.attempted,
      issue: reason,
      canRetry: false,
      canReload: true,
      canAbandon: true,
    });
    this.#emit();
    await this.#runReconciliation(reconcilingPending, token);
  }

  async #runReconciliation(pending: PendingCommit, token: number): Promise<void> {
    if (!this.#isCurrent(token)) return;
    this.#persistence = Object.freeze({
      kind: "reconciling",
      attempted: pending.attempted,
      issue: null,
      canRetry: false,
      canReload: true,
      canAbandon: true,
    });
    this.#emit();

    const loaded = await this.#loadMatch();
    if (!this.#isCurrent(token) || this.#reconciliation !== pending) return;
    if (loaded.kind === "unavailable" || loaded.kind === "invalid") {
      this.#reconciliation = null;
      this.#persistence = this.#degradedPersistence(
        loaded.kind === "unavailable"
          ? loaded.reason
          : storageFailure("corrupt-data", loaded.error.message),
        pending.expected,
        false,
      );
      await this.#completeCommittedPending(pending, token, false);
      return;
    }

    const observedVersion = loadVersion(loaded);
    const nextVersion = pending.next === null ? pending.attempted : versionOf(pending.next.saved);
    const provesNext =
      versionMatches(observedVersion, nextVersion) &&
      ((pending.next === null && loaded.match === null) ||
        (pending.next !== null && loaded.match !== null));
    if (provesNext) {
      const observed = loaded.match === null ? null : liveFromSaved(loaded.match);
      if (
        pending.next !== null &&
        observed !== null &&
        !this.#sameSavedMatch(observed.saved, pending.next.saved)
      ) {
        this.#enterConflict(pending, observedVersion, observed);
        return;
      }
      this.#durableMatch = pending.next;
      this.#durableVersion = nextVersion;
      this.#durableVersionKnown = true;
      this.#persistence = HEALTHY;
      this.#reconciliation = null;
      await this.#completeCommittedPending(pending, token, true);
      return;
    }

    if (versionMatches(observedVersion, pending.expected)) {
      this.#applyUncommittedResolution(pending.uncommitted);
      this.#reconciliation = null;
      this.#presentation = Object.freeze({
        ...this.#presentation,
        activeTransition: null,
      });
      this.#pauseAfterOperation = null;
      this.#routeOrSetup();
      return;
    }

    const external = loaded.match === null ? null : liveFromSaved(loaded.match);
    this.#enterConflict(pending, observedVersion, external);
  }

  async #completeCommittedPending(
    pending: PendingCommit,
    token: number,
    durable: boolean,
  ): Promise<void> {
    if (!this.#isCurrent(token)) return;
    this.#match = pending.next;
    this.#conflict = null;
    this.#reconciliation = null;

    if (pending.next === null) {
      this.#phase = "setup";
      this.#pauseReason = null;
      this.#pauseAfterOperation = null;
      this.#presentation = this.#setupPresentation();
      if (this.#persistence.kind === "degraded") {
        this.#persistence = Object.freeze({ ...this.#persistence, canDiscard: false });
      }
      this.#applyHistoryMutation(pending.history);
      this.#emit();
      return;
    }

    if (this.#ownershipBlocksMutation()) {
      if (pending.presentation !== null) this.#finishPresentation(pending.presentation);
      this.#enterOwnershipConflict(pending.next);
      return;
    }
    this.#ensureOwnershipDegraded();

    if (pending.undoPresentations !== undefined) {
      const lastUndo = pending.undoPresentations.at(-1);
      if (lastUndo === undefined) throw new Error("Undo committed without a presentation recipe.");
      this.#presentation = Object.freeze({
        ...this.#presentation,
        activeTransition: null,
        lastTransition: null,
        focusTarget: Object.freeze({ kind: "board-heading" }),
        announcement: this.#announcement({ kind: "undo", actor: lastUndo.actor }),
      });
      this.#applyHistoryMutation(pending.history);
      this.#finishOrPauseAfterOperation();
      return;
    }

    const presentations = pending.presentation === null ? [] : [pending.presentation];

    if (presentations.length === 0) {
      if (pending.next.result !== null) {
        this.#presentation = Object.freeze({
          ...this.#presentation,
          activeTransition: null,
          focusTarget: Object.freeze({ kind: "result-primary" }),
          announcement: this.#announcement({ kind: "terminal", result: pending.next.result }),
        });
      } else {
        this.#presentation = Object.freeze({
          ...this.#presentation,
          activeTransition: null,
          focusTarget: Object.freeze({ kind: "board-heading" }),
        });
      }
      this.#finishOrPauseAfterOperation();
      this.#applyHistoryMutation(pending.history);
      return;
    }

    if (this.#pauseAfterOperation !== null) {
      for (const presentation of presentations) this.#finishPresentation(presentation);
      this.#finishOrPauseAfterOperation();
      this.#applyHistoryMutation(pending.history);
      return;
    }

    this.#phase = "animating";
    this.#emit();
    for (const presentation of presentations) {
      if (!this.#isCurrent(token)) return;
      this.#presentation = Object.freeze({
        ...this.#presentation,
        activeTransition: presentation,
        focusTarget: presentation.focusAfter,
      });
      this.#emit();
      try {
        await this.#animation.play(presentation);
      } catch {
        // The canonical transition is already committed (or explicitly volatile).
        // Presentation failure must never strand the match.
      }
      if (!this.#isCurrent(token)) return;
      this.#finishPresentation(presentation);
    }
    if (!durable && this.#persistence.kind !== "degraded") {
      throw new Error("A non-durable transition lost its degraded persistence marker.");
    }
    this.#applyHistoryMutation(pending.history);
    this.#routeCurrent();
  }

  #emptyHistory(): readonly MatchHistoryFrame[] {
    return Object.freeze([] as MatchHistoryFrame[]);
  }

  #applyHistoryMutation(mutation: MatchHistoryMutation | null): void {
    if (mutation === null) return;
    switch (mutation.kind) {
      case "append":
        this.#history = Object.freeze([...this.#history, mutation.frame]);
        return;
      case "restore":
        this.#history = Object.freeze([...mutation.history]);
        return;
      case "clear":
        this.#history = this.#emptyHistory();
        return;
    }
  }

  #undoDepth(match: LiveMatch): number {
    if (match.saved.mode === "local") return 1;
    const lastHumanMove = this.#history.findLastIndex(
      (frame) => frame.presentation.actor === "human",
    );
    return lastHumanMove < 0 ? 0 : this.#history.length - lastHumanMove;
  }

  #canUndoForDepth(depth: number): boolean {
    if (this.#match === null) return false;
    if (depth <= 0 || this.#history.length < depth) return false;
    if (
      this.#phase === "setup" ||
      this.#phase === "restoring" ||
      this.#phase === "committing" ||
      this.#phase === "reconciling" ||
      this.#phase === "finished" ||
      (this.#phase === "paused" && this.#pauseReason === "conflict")
    ) {
      return false;
    }
    return true;
  }

  #undoTarget(depth: number): LiveMatch | null {
    const index = this.#history.length - depth;
    const frame = this.#history[index];
    return frame === undefined ? null : frame.live;
  }

  #canUndo(): boolean {
    if (this.#match === null) return false;
    return this.#canUndoForDepth(this.#undoDepth(this.#match));
  }

  #finishOrPauseAfterOperation(): void {
    if (this.#pauseAfterOperation !== null) {
      this.#phase = "paused";
      this.#pauseReason = this.#pauseAfterOperation;
      this.#pauseAfterOperation = null;
      this.#emit();
      return;
    }
    this.#routeCurrent();
  }

  #finishPresentation(presentation: MatchTransitionPresentation): void {
    const result = this.#match?.result ?? null;
    const announcement: MatchAnnouncement =
      result === null
        ? presentation.playback?.direction === "reverse"
          ? this.#announcement({ kind: "undo", actor: presentation.actor })
          : this.#announcement({
              kind: "transition",
              actor: presentation.actor,
              move: presentation.move,
              events: presentation.transition.events,
            })
        : this.#announcement({ kind: "terminal", result });
    this.#presentation = Object.freeze({
      activeTransition: null,
      lastTransition: presentation,
      focusTarget: presentation.focusAfter,
      announcement,
    });
  }

  #finishInterruptedPresentation(): void {
    const active = this.#presentation.activeTransition;
    if (active !== null) this.#finishPresentation(active);
  }

  #routeOrSetup(): void {
    if (this.#match === null) {
      this.#phase = "setup";
      this.#pauseReason = null;
      this.#presentation = this.#setupPresentation();
      this.#emit();
      return;
    }
    this.#routeCurrent();
  }

  #routeCurrent(): void {
    const match = this.#match;
    if (match === null) {
      this.#phase = "setup";
      this.#emit();
      return;
    }
    if (this.#ownershipBlocksMutation()) {
      this.#enterOwnershipConflict(match);
      return;
    }
    this.#ensureOwnershipDegraded();
    this.#selection = cloneSelection({
      mode: match.saved.mode,
      humanPlayer: match.saved.humanPlayer,
      difficulty: match.saved.bot.difficulty,
    });
    this.#selectedPosition = null;
    this.#pauseReason = null;
    const route = routeTurn(match.game, match.result, match.saved.humanPlayer, match.saved.mode);
    if (this.#hidden && route !== "finished") {
      this.#phase = "paused";
      this.#pauseReason = "hidden";
      this.#emit();
      return;
    }
    this.#phase = route;
    if (route === "finished" && this.#presentation.focusTarget?.kind !== "result-primary") {
      this.#presentation = Object.freeze({
        ...this.#presentation,
        focusTarget: Object.freeze({ kind: "result-primary" }),
      });
    }
    this.#emit();
    if (route === "bot-thinking") this.#requestBotMove(match);
  }

  #requestBotMove(requestedMatch: LiveMatch): void {
    if (this.#ownershipBlocksMutation()) {
      this.#enterOwnershipConflict(requestedMatch);
      return;
    }
    const requestedVersion = versionOf(requestedMatch.saved);
    const token = ++this.#operationToken;
    if (requestedMatch.saved.recording?.rulesVersion !== MATCH_RULES_VERSION) {
      const decision = this.#compatibilityBotDecision(requestedMatch);
      if (decision === null) return;
      void Promise.resolve().then(async () => {
        if (!this.#isBotAuthorityCurrent(token, requestedVersion)) return;
        const current = this.#match;
        if (current === null) return;
        const move = this.#canonicalMove(current, decision.move);
        if (move !== null) await this.#applyMoveAndCommit(current, move, "bot", decision);
      });
      return;
    }
    let requestPromise: Promise<BotDecision>;
    try {
      requestPromise = this.#bot.requestMove({
        matchId: requestedVersion.epoch,
        revision: requestedVersion.revision,
        state: requestedMatch.game,
        positionKeys: requestedMatch.positionKeys,
        difficulty: requestedMatch.saved.bot.difficulty,
        seed: this.#seedFactory(requestedVersion.epoch, requestedVersion.revision),
        readAuthority: () => this.#readBotAuthority(requestedMatch),
      });
    } catch {
      requestPromise = Promise.reject(
        new Error("The bot port rejected the request synchronously."),
      );
    }

    void requestPromise.then(
      async (decision) => {
        if (!this.#isBotAuthorityCurrent(token, requestedVersion)) return;
        const current = this.#match;
        if (current === null) return;
        const canonical = this.#canonicalMove(current, decision.move);
        const acceptedDecision =
          canonical === null ? this.#fallbackBotDecision(current, "illegal-response") : decision;
        if (acceptedDecision === null) return;
        const acceptedMove = this.#canonicalMove(current, acceptedDecision.move);
        if (acceptedMove === null) return;
        await this.#applyMoveAndCommit(current, acceptedMove, "bot", acceptedDecision);
      },
      async () => {
        if (!this.#isBotAuthorityCurrent(token, requestedVersion)) return;
        const current = this.#match;
        if (current === null) return;
        const fallback = this.#fallbackBotDecision(current, "worker-error");
        if (fallback === null) return;
        await this.#applyMoveAndCommit(current, fallback.move, "bot", fallback);
      },
    );
  }

  #readBotAuthority(fallback: LiveMatch): Readonly<{
    matchId: string;
    revision: number;
    state: GameState;
    positionKeys: readonly string[];
  }> {
    const current = this.#match;
    if (current === null || this.#disposed) {
      return Object.freeze({
        matchId: "obsolete",
        revision: 0,
        state: fallback.game,
        positionKeys: fallback.positionKeys,
      });
    }
    const version = versionOf(current.saved);
    return Object.freeze({
      matchId: version.epoch,
      revision: version.revision,
      state: current.game,
      positionKeys: current.positionKeys,
    });
  }

  #fallbackBotDecision(
    match: LiveMatch,
    fallbackReason: "illegal-response" | "worker-error",
  ): BotDecision | null {
    const move = selectBotFallbackMove(match.game);
    return move === null ? null : Object.freeze({ move, source: "fallback", fallbackReason });
  }

  #compatibilityBotDecision(match: LiveMatch): BotDecision | null {
    const moves = this.#legalMoves(match);
    const winningMove = moves.find(
      (move) =>
        this.#applyRulesMove(match, move)?.state.outcome?.winner === match.game.activePlayer,
    );
    const move = winningMove ?? moves[0];
    return move === undefined
      ? null
      : Object.freeze({ move, source: "fallback", fallbackReason: "compatibility-rules" });
  }

  #applyRulesMove(match: LiveMatch, move: Move): Transition | null {
    const rulesVersion = match.saved.recording?.rulesVersion;
    if (rulesVersion === LEGACY_MATCH_RULES_VERSION) {
      return applyLegacyRecordedMove(match.game, move);
    }
    if (rulesVersion === PRE_REPETITION_MATCH_RULES_VERSION) {
      return applyVersion2RecordedMove(match.game, move);
    }
    if (rulesVersion === CLEANUP_PASSAGE_MATCH_RULES_VERSION) {
      return applyVersion3RecordedMove(match.game, move);
    }
    const application = applyMove(match.game, move);
    return application.ok ? application.transition : null;
  }

  #isBotAuthorityCurrent(token: number, version: MatchVersion): boolean {
    if (!this.#isCurrent(token) || this.#phase !== "bot-thinking" || this.#match === null) {
      return false;
    }
    return sameMatchVersion(versionOf(this.#match.saved), version);
  }

  #canonicalMove(match: LiveMatch, move: Move): Move | null {
    const notation = moveNotation(move);
    return (
      this.#legalMoves(match).find((candidate) => moveNotation(candidate) === notation) ?? null
    );
  }

  #legalMoves(match: LiveMatch): readonly Move[] {
    const rulesVersion = match.saved.recording?.rulesVersion;
    if (rulesVersion === LEGACY_MATCH_RULES_VERSION) return getLegacyLegalMoves(match.game);
    if (rulesVersion === PRE_REPETITION_MATCH_RULES_VERSION) {
      return getVersion2LegalMoves(match.game);
    }
    if (rulesVersion === CLEANUP_PASSAGE_MATCH_RULES_VERSION) {
      return getVersion3LegalMoves(match.game);
    }
    return getLegalMoves(match.game);
  }

  #advancedLiveMatch(
    prior: LiveMatch,
    game: GameState,
    result: MatchResult | null,
    recording: MatchRecordingV1 | null = prior.saved.recording,
  ): LiveMatch {
    const priorVersion = versionOf(prior.saved);
    const mustRotate = priorVersion.revision >= MAX_SAVED_MATCH_REVISION;
    const epoch = mustRotate ? this.#nextEpoch() : priorVersion.epoch;
    const revision = mustRotate ? 0 : priorVersion.revision + 1;
    return this.#createLiveMatch({
      epoch,
      revision,
      game,
      selection: this.#selection,
      result,
      recording,
    });
  }

  #createLiveMatch(
    input: Readonly<{
      epoch: string;
      revision: number;
      game: GameState;
      selection: MatchSetupSelection;
      result: MatchResult | null;
      recording: MatchRecordingV1 | null;
    }>,
  ): LiveMatch {
    const saved = createSavedMatch({
      epoch: input.epoch,
      revision: input.revision,
      game: input.game,
      mode: input.selection.mode ?? "bot",
      humanPlayer: input.selection.humanPlayer,
      difficulty: input.selection.difficulty,
      controllerResult: input.result,
      recording: input.recording,
    });
    const replay = input.recording === null ? null : replayRecording(input.recording);
    return Object.freeze({
      saved,
      game: input.game,
      result: input.result,
      positionKeys: replay?.positionKeys ?? Object.freeze([positionKey(input.game)]),
    });
  }

  #recordForRestart(match: LiveMatch): MatchRecordV1 {
    const existing = match.saved.recording;
    const recording =
      existing?.completedAt !== null && existing?.completedAt !== undefined
        ? existing
        : completeRecording(
            existing ?? createMatchRecording(match.saved.epoch, match.game),
            this.#clock(),
          );
    if (recording === null) throw new Error("Restart recording unexpectedly remained null.");
    return createMatchRecord({
      recording,
      mode: match.saved.mode,
      humanPlayer: match.saved.humanPlayer,
      difficulty: match.saved.bot.difficulty,
      difficultyPolicyVersion: match.saved.bot.difficultyPolicyVersion,
      result: match.result ?? { kind: "abandoned" },
    });
  }

  #nextEpoch(): string {
    const epoch = this.#epochFactory();
    // createSavedMatch owns the normative validation. Exercise it early enough
    // that no async transition begins with an unusable identity.
    createSavedMatch({
      epoch,
      revision: 0,
      game: createInitialGame(),
      mode: this.#selection.mode ?? "bot",
      humanPlayer: this.#selection.humanPlayer,
      difficulty: this.#selection.difficulty,
    });
    return epoch;
  }

  #enterConflict(
    pending: PendingCommit,
    current: MatchVersion | null,
    displayed: LiveMatch | null,
    origin: ConflictContext["origin"] = "storage",
  ): void {
    this.#cancelWorkerAndAnimation(true);
    this.#reconciliation = null;
    this.#conflict = Object.freeze({ pending, displayed, origin });
    this.#match = displayed ?? pending.prior ?? pending.next;
    this.#durableMatch = displayed;
    this.#durableVersion = current;
    this.#durableVersionKnown = true;
    this.#phase = "paused";
    this.#pauseReason = "conflict";
    this.#pauseAfterOperation = null;
    this.#presentation = Object.freeze({
      ...this.#presentation,
      activeTransition: null,
    });
    this.#persistence = this.#conflictPersistence(
      current,
      pending.next === null ? null : versionOf(pending.next.saved),
      null,
    );
    this.#emit();
  }

  #applyUncommittedResolution(resolution: UncommittedResolution): void {
    this.#match = resolution.match;
    this.#persistence = resolution.persistence;
    this.#durableMatch = resolution.durableMatch;
    this.#durableVersion = resolution.durableVersion;
    this.#durableVersionKnown = resolution.durableVersionKnown;
  }

  #uncommittedResolution(
    match: LiveMatch | null,
    persistence: MatchPersistenceState,
  ): UncommittedResolution {
    return Object.freeze({
      match,
      persistence,
      durableMatch: this.#durableMatch,
      durableVersion: this.#durableVersion,
      durableVersionKnown: this.#durableVersionKnown,
    });
  }

  #tombstoneConflictPending(external: LiveMatch): PendingCommit {
    const expected = versionOf(external.saved);
    return Object.freeze({
      write: Object.freeze({ kind: "tombstone", expected }),
      expected,
      attempted: tombstoneSuccessor(expected),
      prior: external,
      next: null,
      presentation: null,
      uncommitted: Object.freeze({
        match: external,
        persistence: HEALTHY,
        durableMatch: external,
        durableVersion: expected,
        durableVersionKnown: true,
      }),
      history: { kind: "clear" },
    });
  }

  #degradedPersistence(
    reason: StorageFailure,
    durableVersion: MatchVersion | null,
    durableVersionKnown: boolean,
  ): MatchPersistenceState {
    return Object.freeze({
      kind: "degraded",
      reason: Object.freeze({ ...reason }),
      durableVersion: cloneVersion(durableVersion),
      durableVersionKnown,
      canRetry: true,
      canDiscard: this.#match !== null,
    });
  }

  #conflictPersistence(
    current: MatchVersion | null,
    attempted: MatchVersion | null,
    loadIssue: StorageFailure | SavedMatchParseError | null,
  ): MatchPersistenceState {
    return Object.freeze({
      kind: "conflict",
      current: cloneVersion(current),
      attempted: cloneVersion(attempted),
      loadIssue: loadIssue === null ? null : Object.freeze({ ...loadIssue }),
      canTakeOver: true,
      canReload: true,
    });
  }

  #discardLocalToSetup(): void {
    this.#history = this.#emptyHistory();
    this.#match = null;
    this.#selectedPosition = null;
    this.#phase = "setup";
    this.#pauseReason = null;
    this.#pauseAfterOperation = null;
    this.#presentation = this.#setupPresentation();
    if (this.#persistence.kind === "degraded") {
      this.#persistence = Object.freeze({ ...this.#persistence, canDiscard: false });
    } else if (this.#persistence.kind === "invalid-save") {
      this.#persistence = this.#degradedPersistence(
        storageFailure("corrupt-data", "The incompatible saved match was discarded locally."),
        null,
        false,
      );
    }
    this.#emit();
  }

  #canEndAsDraw(): boolean {
    if (this.#match?.result !== null || this.#match.game.outcome !== null) return false;
    return (
      this.#phase === "human-turn" ||
      this.#phase === "bot-thinking" ||
      (this.#phase === "paused" && this.#pauseReason !== "conflict")
    );
  }

  #ownershipBlocksMutation(): boolean {
    return (
      this.#ownershipSnapshot.kind === "read-only" &&
      this.#ownershipSnapshot.reason !== "unavailable" &&
      this.#ownershipSnapshot.reason !== "uncertain"
    );
  }

  #ensureOwnershipDegraded(): void {
    if (
      this.#ownershipSnapshot.kind !== "read-only" ||
      (this.#ownershipSnapshot.reason !== "unavailable" &&
        this.#ownershipSnapshot.reason !== "uncertain") ||
      this.#persistence.kind !== "healthy"
    ) {
      return;
    }
    this.#persistence = this.#degradedPersistence(
      storageFailure(
        "not-supported",
        this.#ownershipSnapshot.reason === "unavailable"
          ? "Cross-context match ownership is unavailable; this match is in-memory only."
          : "Cross-context match ownership could not be proven; this match is in-memory only.",
      ),
      this.#durableVersion,
      this.#durableVersionKnown,
    );
  }

  #enterOwnershipConflict(match: LiveMatch): void {
    if (this.#conflict?.origin === "storage") {
      this.#phase = "paused";
      this.#pauseReason = "conflict";
      this.#emit();
      return;
    }
    const expected = this.#durableVersionKnown ? this.#durableVersion : versionOf(match.saved);
    const pending: PendingCommit = Object.freeze({
      write: Object.freeze({ kind: "replace", expected, next: match.saved }),
      expected,
      attempted: versionOf(match.saved),
      prior: match,
      next: match,
      presentation: null,
      uncommitted: this.#uncommittedResolution(match, this.#persistence),
      history: { kind: "clear" },
    });
    this.#enterConflict(pending, expected, match, "ownership");
  }

  #onOwnershipChanged(snapshot: MatchOwnershipSnapshot): void {
    const next = cloneOwnership(snapshot);
    const previous = this.#ownershipSnapshot;
    if (
      previous.kind === next.kind &&
      previous.reason === next.reason &&
      previous.ownerId === next.ownerId
    ) {
      return;
    }
    this.#ownershipSnapshot = next;
    if (this.#disposed) return;

    if (
      this.#phase === "restoring" ||
      this.#phase === "committing" ||
      this.#phase === "reconciling"
    ) {
      this.#cancelWorkerAndAnimation(false);
      this.#emit();
      return;
    }

    if (next.kind === "owner") {
      this.#emit();
      return;
    }

    if (this.#ownershipBlocksMutation()) {
      this.#cancelTransientWork();
      this.#finishInterruptedPresentation();
      if (this.#match !== null) this.#enterOwnershipConflict(this.#match);
      else this.#emit();
      return;
    }

    this.#ensureOwnershipDegraded();
    if (this.#phase === "bot-thinking" || this.#phase === "animating") {
      this.#cancelTransientWork();
      this.#finishInterruptedPresentation();
      this.#routeCurrent();
      return;
    }
    this.#emit();
  }

  async #acquireOwnership(): Promise<MatchOwnershipSnapshot> {
    let snapshot: MatchOwnershipSnapshot;
    try {
      snapshot = await this.#ownership.acquire();
    } catch {
      snapshot = Object.freeze({ kind: "read-only", reason: "uncertain", ownerId: null });
    }
    this.#onOwnershipChanged(snapshot);
    return this.#ownershipSnapshot;
  }

  async #takeOwnership(): Promise<MatchOwnershipSnapshot> {
    let snapshot: MatchOwnershipSnapshot;
    try {
      snapshot = await this.#ownership.takeOver();
    } catch {
      snapshot = Object.freeze({ kind: "read-only", reason: "uncertain", ownerId: null });
    }
    this.#onOwnershipChanged(snapshot);
    return this.#ownershipSnapshot;
  }

  #cancelTransientWork(): void {
    this.#cancelWorkerAndAnimation(true);
    this.#pauseAfterOperation = null;
  }

  #cancelWorkerAndAnimation(invalidate: boolean): void {
    if (invalidate) this.#operationToken += 1;
    this.#bot.cancel();
    this.#animation.cancel();
  }

  #isCurrent(token: number): boolean {
    return !this.#disposed && token === this.#operationToken;
  }

  #sameSavedMatch(left: SavedMatchV3, right: SavedMatchV3): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
  }

  async #loadMatch(): Promise<LoadMatchResult> {
    try {
      return await this.#storage.loadMatch();
    } catch {
      return Object.freeze({
        kind: "unavailable",
        reason: storageFailure(
          "read-failed",
          "Match storage failed without returning a readable result.",
        ),
      });
    }
  }

  #setupPresentation(): MatchPresentation {
    return Object.freeze({
      activeTransition: null,
      lastTransition: null,
      focusTarget: Object.freeze({ kind: "setup-primary" }),
      announcement: null,
    });
  }

  #announcement(value: AnnouncementInput): MatchAnnouncement {
    this.#announcementSequence += 1;
    switch (value.kind) {
      case "selection":
        return Object.freeze({ ...value, sequence: this.#announcementSequence });
      case "transition":
        return Object.freeze({ ...value, sequence: this.#announcementSequence });
      case "undo":
        return Object.freeze({ ...value, sequence: this.#announcementSequence });
      case "terminal":
        return Object.freeze({ ...value, sequence: this.#announcementSequence });
    }
  }

  #buildSnapshot(): MatchControllerSnapshot {
    const match = this.#match;
    const legalMoves =
      this.#phase === "human-turn" && match !== null && match.result === null
        ? this.#legalMoves(match)
        : Object.freeze([] as Move[]);
    return Object.freeze({
      phase: this.#phase,
      game: match?.game ?? null,
      authority: match === null ? null : versionOf(match.saved),
      mode: match?.saved.mode ?? null,
      humanPlayer: match?.saved.humanPlayer ?? null,
      botPlayer:
        match === null || match.saved.mode === "local" ? null : opponentOf(match.saved.humanPlayer),
      difficulty:
        match === null || match.saved.mode === "local" ? null : match.saved.bot.difficulty,
      result: match?.result ?? null,
      historyId: match?.saved.recording?.id ?? null,
      recording: match?.saved.recording ?? null,
      setupSelection: cloneSelection(this.#selection),
      selectedPosition:
        this.#selectedPosition === null
          ? null
          : coordinate(this.#selectedPosition.row, this.#selectedPosition.column),
      legalMoves: Object.freeze([...legalMoves]),
      persistence: freezePersistence(this.#persistence),
      ownership: cloneOwnership(this.#ownershipSnapshot),
      presentation: this.#presentation,
      pauseReason: this.#pauseReason,
      pauseRequested: this.#pauseAfterOperation !== null,
      canUndo: this.#canUndo(),
      review: this.#reviewTimeline(),
      disposed: this.#disposed,
    });
  }

  #emit(): void {
    if (this.#disposed) return;
    this.#snapshot = this.#buildSnapshot();
    for (const listener of this.#listeners) listener();
  }
}
